import React from 'react'
import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, makeStyles } from '@material-ui/core'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { DailyRegistration, DistributionItem, MonthlyAverage } from '../types'
import { colorForLabel, percentage } from '../utils'

const useStyles = makeStyles(theme => ({
  card: { padding: theme.spacing(2), minWidth: 0 },
  title: { fontWeight: 700 },
  description: { color: '#66707a', marginBottom: theme.spacing(1) },
  chart: { width: '100%', height: 330 },
  chartWithSummary: { position: 'relative' },
  chartSummary: {
    position: 'absolute',
    top: '43%',
    left: '50%',
    maxWidth: 130,
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    fontWeight: 700,
    pointerEvents: 'none'
  },
  emptySummary: {
    textAlign: 'center',
    fontWeight: 700,
    marginTop: theme.spacing(2)
  },
  tableWrap: { maxHeight: 220, overflow: 'auto', marginTop: theme.spacing(1) },
  empty: {
    height: 240,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#66707a'
  }
}))

const DataTable: React.FC<{
  headers: string[]
  rows: (string | number)[][]
  label: string
}> = ({ headers, rows, label }) => {
  const classes = useStyles()
  return (
    <Box className={classes.tableWrap}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <TableCell key={header}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row[0]}-${index}`}>
              {row.map((value, cell) => (
                <TableCell key={`${index}-${cell}`}>{value}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

const ChartCard: React.FC<{
  title: string
  description: string
  children: React.ReactNode
}> = ({ title, description, children }) => {
  const classes = useStyles()
  return (
    <Paper className={classes.card} component="section" aria-label={title}>
      <Typography className={classes.title} variant="h6">
        {title}
      </Typography>
      <Typography className={classes.description} variant="body2">
        {description}
      </Typography>
      {children}
    </Paper>
  )
}

export const DailyChart: React.FC<{ data: DailyRegistration[] }> = ({ data }) => {
  const classes = useStyles()
  return (
    <ChartCard title="Registros por día" description="Casos ahorradores registrados en cada día calendario del mes.">
      <Box className={classes.chart} role="img" aria-label="Gráfica de barras de registros diarios">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 8, left: -18, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis allowDecimals={false} />
            <Tooltip
              labelFormatter={day => `Día ${day}`}
              formatter={(value: number) => [`${value} casos`, 'Registros']}
            />
            <Bar dataKey="count" name="Registros" fill="#F56600" radius={[5, 5, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <DataTable
        label="Resumen textual de registros diarios"
        headers={['Fecha', 'Casos']}
        rows={data.map(item => [item.date, item.count])}
      />
    </ChartCard>
  )
}

export const MonthlyAverageChart: React.FC<{ data: MonthlyAverage[] }> = ({ data }) => {
  const classes = useStyles()
  return (
    <ChartCard
      title="Promedio diario por mes"
      description="Promedio sobre días hábiles de lunes a viernes; el numerador incluye fines de semana."
    >
      <Box className={classes.chart} role="img" aria-label="Gráfica de barras del promedio diario mensual">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 8, left: -8, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip
              formatter={(value: number, _name: string, props: any) => [
                `${Number(value).toFixed(1)} (${props.payload.count} casos / ${
                  props.payload.businessDayCount
                } días hábiles)`,
                'Promedio'
              ]}
            />
            <Bar dataKey="average" name="Promedio diario" fill="#1565C0" radius={[5, 5, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <DataTable
        label="Resumen textual de promedios mensuales"
        headers={['Mes', 'Casos', 'Días hábiles', 'Promedio']}
        rows={data.map(item => [item.label, item.count, item.businessDayCount, item.average.toFixed(1)])}
      />
    </ChartCard>
  )
}

export const DistributionChart: React.FC<{
  title: string
  description: string
  data: DistributionItem[]
  summary?: string
}> = ({ title, description, data, summary }) => {
  const classes = useStyles()
  if (!data.length) {
    return (
      <ChartCard title={title} description={description}>
        {summary && <Typography className={classes.emptySummary}>{summary}</Typography>}
        <Box className={classes.empty}>No hay datos para este período.</Box>
      </ChartCard>
    )
  }
  return (
    <ChartCard title={title} description={description}>
      <Box
        className={`${classes.chart} ${summary ? classes.chartWithSummary : ''}`}
        role="img"
        aria-label={`Diagrama de torta: ${title}`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="43%"
              innerRadius={summary ? '42%' : 0}
              outerRadius="68%"
            >
              {data.map(item => (
                <Cell key={item.label} fill={colorForLabel(item.label)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, _name: string, props: any) => [
                `${value} casos (${percentage(Number(value), data).toFixed(1)}%)`,
                props.payload.label
              ]}
            />
            <Legend verticalAlign="bottom" />
          </PieChart>
        </ResponsiveContainer>
        {summary && <Typography className={classes.chartSummary}>{summary}</Typography>}
      </Box>
      <DataTable
        label={`Resumen textual de ${title}`}
        headers={['Categoría', 'Casos', 'Porcentaje']}
        rows={data.map(item => [item.label, item.count, `${percentage(item.count, data).toFixed(1)}%`])}
      />
    </ChartCard>
  )
}
